import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ThemeProvider, CssBaseline, Box, Tabs, Tab, Badge, Typography, Collapse, Dialog, IconButton } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Close as CloseIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { theme } from "../../theme";
import { RemindersListComponent } from "./components/RemindersListComponent";
import { ShipmentActionsComponent } from "./components/ShipmentActionsComponent";
import { ShipmentDetails } from "./components/ShipmentDetails";
import { EventsTimeline } from "../../../eventsTimelineComponent";
import type { TimelineEvent } from "../../../eventsTimelineComponent/types";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import { useShipmentFeed } from "./hooks/useShipmentFeed";
import { useShipmentTimeline } from "./hooks/useShipmentTimeline";
import type {
  Reminder,
  GetShipmentFeedResponse,
  GetTimelineEventsResponse,
  GetShipmentActionsResponse,
} from "@services/demo/msoffice/types";

interface DemoShipmentFeedPageProps {
  onBack: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ChatComponent?: React.ComponentType<any>;
}

export const DemoShipmentFeedPage: React.FC<DemoShipmentFeedPageProps> = ({
  onBack,
  ChatComponent,
}) => {
  const { currentMessageId, isReady, apiClient, actions } = useOutlookPlugin();

  const fetchShipmentFeed = useCallback(
    (messageId: string) =>
      apiClient.get<GetShipmentFeedResponse>(
        `/msoffice-api/demo/outlook/shipment_feed?messageId=${encodeURIComponent(messageId)}`
      ),
    [apiClient]
  );

  const fetchShipmentTimeline = useCallback(
    (shipmentId: string) =>
      apiClient.get<GetTimelineEventsResponse>(
        `/msoffice-api/demo/outlook/shipment_feed_timeline?shipmentId=${encodeURIComponent(shipmentId)}`
      ),
    [apiClient]
  );

  const fetchShipmentActions = useCallback(
    (shipmentId: string) =>
      apiClient.get<GetShipmentActionsResponse>(
        `/msoffice-api/demo/outlook/shipment_actions?shipmentId=${encodeURIComponent(shipmentId)}`
      ),
    [apiClient]
  );

  const fetchReminders = useCallback(
    () =>
      apiClient.get<{ data: Reminder[] }>("/msoffice-api/demo/outlook/reminders"),
    [apiClient]
  );

  const {
    shipment,
    loading: shipmentsLoading,
    hasShipment,
  } = useShipmentFeed(currentMessageId, { fetchShipmentFeed });

  const {
    events: timelineEvents,
    loading: timelineLoading,
    error: timelineError,
  } = useShipmentTimeline(
    shipment?.id ?? null,
    fetchShipmentTimeline
  );

  const [activeTab, setActiveTab] = useState(0);
  const [actionsCount, setActionsCount] = useState(0);
  const [highlightedEmailId, setHighlightedEmailId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [previewEvent, setPreviewEvent] = useState<TimelineEvent | null>(null);

  // Ensure every event has an emailId so all timeline bubbles are clickable
  const processedEvents = useMemo(
    () =>
      timelineEvents.map((event, idx) => ({
        ...event,
        emailId: event.emailId || `__evt_${idx}`,
      })),
    [timelineEvents]
  );

  // Reset to shipment tab when a new shipment becomes available
  useEffect(() => {
    if (hasShipment && !shipmentsLoading) {
      setActiveTab(0);
    }
  }, [hasShipment, shipmentsLoading]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenEmail = (emailId: string) => {
    // Find the clicked event and show preview dialog
    const event = processedEvents.find((e) => e.emailId === emailId);
    if (event) {
      setPreviewEvent(event);
    }
    // For events with real emailIds, also open via the adapter (e.g. Outlook)
    if (!emailId.startsWith("__evt_")) {
      actions.openEmail(emailId, "Timeline Email");
    }
  };

  // Called from chat citation clicks - switches to feed tab and highlights the message
  const handleScrollToEmail = (emailId: string) => {
    if (hasShipment) {
      setActiveTab(0);
      // Clear then set to re-trigger if same email clicked twice
      setHighlightedEmailId(null);
      setTimeout(() => setHighlightedEmailId(emailId), 50);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
        {/* Tabs Navigation - sticky, doesn't scroll */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              bgcolor: "black",
              minHeight: "48px",
              "& .MuiTab-root": {
                color: "rgba(255, 255, 255, 0.7)",
                minHeight: "48px",
              },
              "& .Mui-selected": {
                color: "#FEDF33 !important",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#FEDF33",
              },
            }}
          >
            {hasShipment && <Tab label="Shipment Feed" />}
            <Tab
              label={
                <Badge
                  badgeContent={actionsCount}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#FEDF33",
                      color: "black",
                      fontWeight: "bold",
                      right: "-8px",
                    },
                  }}
                >
                  All Actions
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Tab Content - scrollable area */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* Shipment Feed Tab */}
          {hasShipment && activeTab === 0 && !shipmentsLoading && (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {/* Collapsible Shipment Details drawer */}
              {shipment && (
                <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider" }}>
                  <Box
                    onClick={() => setDetailsOpen(!detailsOpen)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 0.75,
                      cursor: "pointer",
                      bgcolor: "grey.50",
                      "&:hover": { bgcolor: "grey.100" },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                      {shipment.properties?.find((p: any) => p.name === "booking_number")?.value || shipment.id}
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 20,
                        color: "text.secondary",
                        transition: "transform 0.2s",
                        transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </Box>
                  <Collapse in={detailsOpen}>
                    <Box sx={{ px: 1, pb: 1 }}>
                      <ShipmentDetails
                        shipment={{
                          id: shipment.id,
                          properties: shipment.properties,
                        } as any}
                        maxVisibleDetails={4}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Actions section */}
              {isReady && shipment && (
                <Box sx={{ flexShrink: 0 }}>
                  <ShipmentActionsComponent
                    shipmentId={shipment.id}
                    fetchActions={fetchShipmentActions}
                  />
                </Box>
              )}

              {/* Timeline section */}
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: "grey.50",
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                  Timeline
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <EventsTimeline
                  events={processedEvents}
                  loading={timelineLoading}
                  error={timelineError}
                  onOpenEmail={handleOpenEmail}
                  numberOfLanes={4}
                  filtersCollapsible
                  highlightedEmailId={highlightedEmailId}
                />
              </Box>
            </Box>
          )}

          {/* All Actions Tab (was Reminders) */}
          {activeTab === (hasShipment ? 1 : 0) && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}>
              <RemindersListComponent
                onUrgentCountChange={setActionsCount}
                fetchReminders={fetchReminders}
              />
            </Box>
          )}
        </Box>

        {/* Floating Chat Component - Only show on Shipment Feed tab */}
        {hasShipment && activeTab === 0 && shipment && ChatComponent && (
          <ChatComponent
            conversationType="shipment_question"
            extraParams={{
              feedId: shipment.id,
            }}
            onScrollToEmail={handleScrollToEmail}
          />
        )}
        {/* Email Preview Dialog */}
        <Dialog
          open={!!previewEvent}
          onClose={() => setPreviewEvent(null)}
          maxWidth="sm"
          fullWidth
          slotProps={{ paper: { sx: { maxHeight: "80vh" } } }}
        >
          {previewEvent && (
            <Box sx={{ p: 0 }}>
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, mr: 1 }}>
                  {previewEvent.emailSubject || previewEvent.name}
                </Typography>
                <IconButton size="small" onClick={() => setPreviewEvent(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Email metadata */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50" }}>
                {previewEvent.emailFrom && previewEvent.emailFrom.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>From:</strong> {previewEvent.emailFrom.join(", ")}
                  </Typography>
                )}
                {previewEvent.emailTo && previewEvent.emailTo.length > 0 && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>To:</strong> {previewEvent.emailTo.join(", ")}
                  </Typography>
                )}
                {previewEvent.date && (
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(previewEvent.date), "EEEE, MMMM d, yyyy 'at' h:mm a")}
                  </Typography>
                )}
              </Box>

              {/* Email body */}
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {previewEvent.preview || previewEvent.snippet || previewEvent.name}
                </Typography>
              </Box>

              {/* Attachments */}
              {previewEvent.attachments && previewEvent.attachments.length > 0 && (
                <Box sx={{ px: 2, pb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {previewEvent.attachments.map((att) => (
                    <Box
                      key={att.emailAttachmentId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        fontSize: "12px",
                        color: "text.secondary",
                      }}
                    >
                      {att.filename || "Attachment"}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};
